/** @fileoverview Button that runs one action of the app, used for screen buttons and list header buttons. */

import { LoaderCircle } from 'lucide-react';

import { Button } from '../ui/button';

import type {
  FabrikButtonStyle,
  FabrikComponent,
  FabrikComponentSize,
  InputSnapshot,
} from '../../types/apps';
import {
  getActionTargetIds,
  useComponentStateStore,
  useIsActionBlocked,
} from './componentStateStore';
import DoneTick from './DoneTick';
import { findStateComponent, SAVE_APP_NOTE } from './helpers';
import InlineAlert from './InlineAlert';
import type { FieldValues } from './types';
import ActionConfirmDialog from './ActionConfirmDialog';
import { appButtonLook } from './appButton';
import { useRunAction } from './useRunAction';

type ActionButtonProps = {
  actionId: string;
  label: string;
  variant?: FabrikButtonStyle['variant'];
  size?: FabrikComponentSize;
  fields: FieldValues;
  inputs: InputSnapshot;
  screenId: string;
  screenComponents: FabrikComponent[];
  /* Lines the button and its error up on the right, for buttons in a right-aligned row. */
  alignEnd?: boolean;
  selectionPath?: string;
};

/**
 * Button that runs an action for the screen, then reloads the checklists and timelines it writes
 * to. Disabled while the action runs, so one press runs it once. Hidden when the viewer is in
 * none of the action's allowed groups.
 * @param props action id, button label, look and size, the record fields, the screen's components and the alignment
 */
export default function ActionButton({
  actionId,
  label,
  variant,
  size,
  fields,
  inputs,
  screenId,
  screenComponents,
  alignEnd = false,
  selectionPath,
}: ActionButtonProps) {
  const { actions } = useComponentStateStore();
  const isBlocked = useIsActionBlocked(actionId);
  const { canRun, isBusy, isDone, error, runAction, confirmation } = useRunAction();
  if (isBlocked) {
    return null;
  }
  const actionTargets = getActionTargetIds(actions, actionId).flatMap((componentId) => {
    const target = findStateComponent(screenComponents, componentId);
    return target ? [target] : [];
  });

  return (
    <div
      data-fabrik-selection-path={selectionPath}
      className={alignEnd ? 'flex flex-col items-end text-right' : undefined}
    >
      <Button
        type="button"
        {...appButtonLook({ variant, size })}
        disabled={!canRun || isBusy}
        aria-busy={isBusy || undefined}
        title={canRun ? undefined : SAVE_APP_NOTE}
        onClick={() => runAction({ actionId, screenId, fields, inputs, targets: actionTargets })}
      >
        {isBusy && <LoaderCircle className="animate-spin" aria-hidden="true" />}
        <DoneTick isShown={isDone} />
        <span data-fabrik-selection-path={selectionPath ? `${selectionPath}/label` : undefined}>
          {label}
        </span>
      </Button>
      {error && <InlineAlert className="mt-1">{error}</InlineAlert>}
      <ActionConfirmDialog {...confirmation} />
    </div>
  );
}
